import { createAction, emptyProps, props } from '@ngrx/store';
import { Map, MapWithState, MapSettings, Layer, SectionType, MapLayerView, MapLink, LayerDocuments, LayerDocumentUIdsSet } from './models';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { LayerDataChangeEvent } from './models/layer-data-change-event.model';
import { QueryParams, QueryResult } from 'src/app/modules/core/store/models/query.model';
import { Share } from 'src/app/modules/core/store/models/content-share.model';
import { Document } from 'src/app/modules/core/store/models/document.model';
import { DatasetChangeEvent } from './models/dataset-change-event.model';
import { WorkflowChangeEvent } from './models/workflow-change-event.model';
import { SiteWatchLayer } from 'src/app/modules/site-watch/store/site-watch.model';

// core COP actions
export const loadSelectedMapAndDocument = createAction(
  '[COP/Page] Load Selected Map and Document',
  props<{ mapUId: string; datasetUId?: string; documentUId?: string }>()
);

// map actions
export const loadMapsForUserCompleteToStore = createAction('[COP/API/Store] Load Maps for User Complete', props<{ maps: Map[] }>());
export const loadMapWithStateCompleteToStore = createAction(
  '[COP/API/Store] Load Map With State Complete',
  props<{ mapWithState: MapWithState }>()
);
export const loadDatasetsForSelectedMapCompleteToStore = createAction(
  '[COP/API/Store] Load Datasets For Selected Map Complete',
  props<{ datasets: Dataset[] }>()
);
export const setFloorsForSelectedMap = createAction('[COP/Store] Set Floors For Selected Map', props<{ floorValues: string[] }>());
export const storeCreatedMapToStore = createAction(
  '[COP/API/Store] Store Created Map',
  props<{ mapState: MapWithState; cancelEdit: boolean }>()
);
export const updateMapCompleteToStore = createAction(
  '[COP/API/Store] Update Map Complete',
  props<{ mapState: MapWithState; cancelEdit: boolean }>()
);
export const loadDatasetsForSelectedMap = createAction('[COP/Worker] Load Datasets For Selected Map');
export const loadMapsForUserAndSelect = createAction(
  '[COP/API] Load Maps for User And Select',
  props<{ mapUId?: string; datasetUId?: string; documentUId?: string }>()
);
export const createMap = createAction('[COP/API] Create Map', props<{ map: Map; cancelEdit: boolean }>());
export const updateMap = createAction(
  '[COP/API] Update Map',
  props<{ mapState: MapWithState; cancelEdit: boolean; refreshLayersOnly?: LayerDocumentUIdsSet; refreshLabelsOnly?: boolean }>()
);

export const removeSelectedMapDrawingAndDocumentsForLayer = createAction(
  '[COP/API] Remove Layer Documents',
  props<{ layerUId: string; then: any[] }>()
);

export const updateMapDrawingDocumentsForLayer = createAction(
  '[COP/UI] Update Layer Documents',
  props<{ layerUId: string; documents?: Document[] }>()
);

export const refreshLayerDocuments = createAction(
  '[COP/UI] Refresh Layers Documents',
  props<{ documents: Document[]; dataset: Dataset; layerUId?: string }>()
);

export const removeLayerDocuments = createAction(
  '[COP/UI] Remove Layers Documents',
  props<{ documents: Document[]; dataset: Dataset; layerUId?: string }>()
);

export const transitionDocumentWorkflows = createAction(
  '[COP/Page] Transition Document Workflows',
  props<{ layerUId: string; workflowStepUId: string; documents: Document[]; datasetUId?: string }>()
);
export const deleteLayerDocuments = createAction('[COP/Page] Delete Layer Documents', props<{ layerUId: string; documents: Document[] }>());

export const removeSelectedMapDrawingAndDocumentsForFolder = createAction(
  '[COP/API] Remove Folder Documents',
  props<{ folderUId: string; then: any[] }>()
);
export const refreshLayerDocumentsOnMap = createAction(
  '[COP/Page] Refresh Layer Documents On Map',
  props<{ layerUId: string; documentUIds?: string[] }>()
);

export const loadDocumentsForMapLayer = createAction('[COP/API] Load Layer Documents', props<{ layerUId: string }>());
export const populateAndTrackSelectedMap = createAction('[COP/Worker] Populate and Track Selected Map');
export const processLayerDataChangeEvents = createAction(
  '[COP/Worker] Process Layer Data Change Events',
  props<{ events: LayerDataChangeEvent[] }>()
);
export const processSignalRDatasetChangeEvent = createAction(
  '[COP/Worker] Process SignalR Dataset Change',
  props<{ event: DatasetChangeEvent }>()
);
export const processSignalRWorkflowChangeEvent = createAction(
  '[COP/Worker] Process SignalR Workflow Change',
  props<{ event: WorkflowChangeEvent }>()
);
export const checkDocumentsMatchLayerFilter = createAction('[COP/Worker] Check Documents Match Layer Filter');

//
// map UI
//
export const setLayersLoading = createAction('[COP/UI/Store] Set Layers Loading', props<{ layerUIds: string[] }>());
export const setLayerLoadingOnToStore = createAction('[COP/UI/Store] Set Layer Loading On', props<{ layerUId: string }>());
export const setLayerLoadingOffToStore = createAction(
  '[COP/UI/Store] Set Layer Loading Off',
  props<{ layerUId: string; isError?: boolean }>()
);
export const setSelectedMap = createAction('[COP/UI] Set Selected Map', props<{ uId?: string }>());
export const setUserMapSettingsToLoadToStore = createAction(
  '[COP/UI/Store] Load Settings from User Store',
  props<{ mapSettings: MapSettings; selectedMapUId: string }>()
);
export const refreshSelectedMap = createAction('[COP/UI] Refresh Selected Map');

export const editSelectedMapToStore = createAction('[COP/UI/Store] Edit Selected Map');
export const cancelEditMap = createAction('[COP/UI] Cancel Edit Map');
export const setCancelEditMapToStore = createAction('[COP/UI/Store] Set Cancel Edit Map');
export const removeUnsavedMapAfterCancel = createAction('[COP/UI/Store] Remove Unsaved Map');
export const showNewMapToStore = createAction('[COP/UI/Store] Show New Map');
export const newMap = createAction(
  '[COP/UI] New Map',
  props<{
    values?: {
      name: string;
      description?: string;
      displaySettings?: string;
      sections: { value: number; type: SectionType; name: string }[];
    };
  }>()
);
export const newMapCompleteToStore = createAction('[COP/UI/Store] New Map Complete', props<{ newMapState: MapWithState }>());
export const cancelNewMapToStore = createAction('[COP/UI/Store] Cancel New Map');
export const copyMap = createAction('[COP/UI] Copy Map', props<{ map?: Map }>());
export const editMap = createAction('[COP/UI] Edit Map', props<{ uId?: string }>());
export const editMapWithStateCompleteToStore = createAction(
  '[COP/UI/Store] Edit Map With State Complete',
  props<{ mapWithState: MapWithState }>()
);
export const copySelectedMap = createAction('[COP/UI] Copy Selected Map', props<{ mapName?: string }>());
export const setMapForDeleteToStore = createAction('[COP/UI/Store] Set Selected Map For Delete', props<{ uId: string }>());
export const clearMapForDeleteToStore = createAction('[COP/UI/Store] Clear Selected Map For Delete');
export const getMapAndCopy = createAction('[COP/UI] Get Map Definition and Copy Map', props<{ uId?: string }>());
export const deleteSelectedMap = createAction('[COP/UI] Delete Selected Map');
export const deleteSelectedMapCompleteToStore = createAction('[COP/UI/Store] Delete Selected Map Complete');
export const saveSelectedMap = createAction(
  '[COP/UI] Save Selected Map',
  props<{ mapName: string; cancelEdit: boolean; refreshLayersOnly?: LayerDocumentUIdsSet; refreshLabelsOnly?: boolean }>()
);
export const setUiStateFromStoredSettingsToStore = createAction('[COP/UI/Store] Set UI State From Settings from User Store');
export const saveUserState = createAction('[COP/UI] Save User Settings');
export const restoreUserState = createAction('[COP/UI] Restore User Settings');
export const addSelectedDocumentToStore = createAction(
  '[COP/UI/Store] Add Selected Document',
  props<{ document; clearExisting: boolean }>()
);
export const removeSelectedDocumentToStore = createAction('[COP/UI/Store] Remove Selected Document', props<{ document }>());
export const clearSelectedDocumentsToStore = createAction('[COP/UI/Store] Clear Selected Documents');
export const enableHistoryTrackForDocument = createAction('[COP/UI/Store] Enable History Track for Document', props<{ documentUId: string }>());
export const disableHistoryTrackForDocument = createAction('[COP/UI/Store] Disable History Track for Document', props<{ documentUId: string }>());
export const goToMap = createAction('[COP/UI] Go to Map', props<{ map: Map }>());
export const setSelectedMapType = createAction('[COP/UI] Set Selected Map Type', props<{ mapType: string }>());
export const toggleTraffic = createAction('[COP/UI] Toggle Traffic Layer', props<{ isTraffic: boolean }>());
export const toggleTransit = createAction('[COP/UI] Toggle Transit Layer', props<{ isTransit: boolean }>());
export const toggleLabel = createAction('[COP/UI] Toggle Labels For Satellite Map', props<{ isLabeled: boolean }>());
export const togglePOI = createAction('[COP/UI] Toggle POI on the Map', props<{ isPOI: boolean }>());
export const setEditMapWallToStore = createAction('[COP/UI/Store] Set Edit Map Wall', props<{ isEdit: boolean }>());
export const showNightLayerToStore = createAction('[COP/UI/Store] Show Night Layer');
export const hideNightLayerToStore = createAction('[COP/UI/Store] Refresh Night Layer');
export const clearMap = createAction('[COP/UI] Clear Current Map', props<{ options?: any }>());
export const setIsMaploadingToStore = createAction('[COP/UI/Store] Set Map Is Loading', props<{ loading: boolean }>());
export const zoomToDocument = createAction('[COP/UI] Zoom To Document', props<{ layer: Layer; dataset: Dataset; document: Document }>());
export const zoomToDocumentByUIds = createAction('[COP/UI] Zoom To Document By UIds', props<{ datasetUId: string; documentUId: string }>());
export const getTileLayerDocumentsFromMapClick = createAction(
  '[COP/UI] Get Tile Layer Documents From Map Click',
  props<{ layers: Layer[]; lat: number; lng: number }>()
);
export const getTileLayerDocumentsFromMapClickComplete = createAction(
  '[COP/UI] Get Tile Layer Documents From Map Click Complete',
  props<{ results: any }>()
);

export const ensureDocumentDatasetsLoaded = createAction('[COP/Api] Ensure Document Datasets Loaded', props<{ datasetUIds: string[] }>());
export const addDocumentDataset = createAction('[COP/Api] Ensure Document Dataset Loaded Complete', props<{ datasetUId: string }>());
export const addDatasetToStore = createAction('[COP/Page/Store] Add Dataset To State', props<{ dataset: any }>());
export const removeDatasetFromStore = createAction('[COP/Page/Store] Remove Dataset From State', props<{ dataset: Dataset }>());

export const addGhostLayerToStore = createAction(
  '[COP/Page/Store] Add Ghost Layer',
  props<{ layerUId: string; datasetUId: string; document: Document }>()
);
export const removeGhostLayerFromStore = createAction(
  '[COP/Page/Store] Remove Ghost Layer',
  props<{ layerUId: string }>()
);
export const createSiteWatchLayerAndSelectDocumentAndDataset = createAction(
  '[COP/Page] Create Layer and select Dataset and Document',
  props<{ sourceLayer: SiteWatchLayer; datasetUId: string; document: Document; setBackAction: boolean }>()
);

export const setLayerVisibilitiesFromCurrentLayerState = createAction('[COP/UI/Store] Set layer visibilites from layer states');

export const setLayerVisibilityForLayerDocuments = createAction('[COP/UI] Set layer visibility for layer document ');

// link document
export const setLinkDocumentToStore = createAction(
  '[COP/UI/Store] Set Link Document',
  props<{ datasetUId?: string; documentUId?: string }>()
);

//
// error handling
//
export const setError = createAction('[COP/API] Set Error', props<{ err: any }>());

//
// Map Room Actions
//
export const hideMapRoomToStore = createAction('[COP/UI/Store] Hide Map Room');
export const showMapRoomToStore = createAction('[COP/UI/Store] Show Map Room');
export const shareMapLink = createAction('[COP/Api] Share Map Link', props<{ uId: string }>());
export const shareMapLinkCompleteToStore = createAction('[COP/Api/Store] Share Map Link Complete', props<{ mapLink: MapLink }>());
export const queryForMapLinks = createAction('[COP/API] Get Map Links', props<{ params: Partial<QueryParams> }>());
export const queryForMapLinksCompleteToStore = createAction(
  '[COP/API/Store] Get Map Links Complete',
  props<{ results: QueryResult<MapLink> }>()
);
export const deleteMapLink = createAction('[COP/Api] Delete Map Link', props<{ uId: string }>());
export const deleteMapLinkCompleteToStore = createAction('[COP/Api/Store] Delete Map Link Complete', props<{ uId: string }>());
export const getMapRoomCollaborators = createAction('[COP/Api] Get Map Room Collaborators', props<{ contentUIds: string[] }>());
export const getMapRoomCollaboratorsCompleteToStore = createAction(
  '[COP/Api/Store] Get Map Room Collaborators Complete',
  props<{ results: Share[] }>()
);
export const refreshMapRoom = createAction('[COP/API] Refresh Maps List for Map Room', props<{ mapUId?: string }>());
export const refreshMapRoomCompleteToStore = createAction(
  '[COP/API/Store] Refresh Map Room Complete',
  props<{ results: QueryResult<Map>; selectedMapUId: string }>()
);

export const setOverviewModeToStore = createAction('[COP/UI/Store] Set Overview Mode');
export const setMapDirtyToStore = createAction('[COP/UI/Store] Set Map Dirty Flag');

export const checkDataChanged = createAction(
  '[COP/UI] Checks if data update needs to be updated',
  props<{ layerUId: string; documentUIds: string[]; action: 'add' | 'update' | 'delete' }>()
);
export const checkClusterDataChanged = createAction(
  '[COP/UI] Check Cluster Documents Data Changed',
  props<{ layerDocuments: LayerDocumentUIdsSet; position: any }>()
);

export const removeMapFromStore = createAction('[COP/UI] Remove Map From State', props<{ mapUId: string }>());

//
// floor filter handling
//
export const setFloorFilter = createAction('[COP/UI] Set Floor for Filter', props<{ floor: string }>());
export const setFloorFilterComplete = createAction('[COP/UI] Set Floor for Filter Complete', props<{ floor: string }>());
export const clearFloorValues = createAction('[COP/UI] Clear Floor Values on  Map');

export * from '../lsd/lsd.actions';
